import { eventEmitter } from "../eventEmitters/EventEmitters";
import storage from "../util/storage";
import { FunnelMode } from "./funnelProgress";

/*
 * Funnel step indicators: what the user has set up, and the "click your pages in order" picking mode. A plain module plus the app's eventEmitter, which is this codebase's convention for
 * UI state (playbackModeService.ts, clusterService.ts's selectedFilter) rather than a Redux slice. The design of record is docs/FUNNEL_STEP_INDICATORS.md.
 *
 * THE FUNNEL is the steps the user tagged, IN ORDER, NOT including the goal: the goal is always the last step and is read from the site's goal (setGoalProvider), never stored here.
 *
 * DEMO AND STAGING ONLY for now: `?funnel=1` turns it on for this browser, `?funnel=0` turns it off. Everything that imports this module does so behind the build switch
 * (`process.env.REACT_APP_DEMO === "1" || process.env.REACT_APP_FUNNEL === "1"`, funnelGating.test.ts), so it never reaches a production bundle.
 *
 * WHAT IS SAVED IS PER SITE. The steps and the mode describe ONE site's pages, and an account can own several sites, so they are stored under a key that carries the site id (the same
 * rule as the pages cache: one global key would show one site's funnel on another). A funnel saved before this was per site sits under the bare key; it is read once as a fallback (the
 * demo, which has one site, wrote it) and is never written again.
 */
const ENABLED_KEY = "skydive.funnelEnabled";
const STEPS_KEY = "skydive.funnel.steps";
const VIEW_KEY = "skydive.funnel.view";
const MODE_KEY = "skydive.funnel.mode";

let enabled: boolean | null = null;
let steps: string[] = [];
let draft: string[] = [];
let building = false;
/** the storage key `steps` was last loaded from: a different site means a different funnel */
let loadedFrom: string | null = null;
let goalProvider: () => string | null = () => null;

/** ".<siteId>" for the site being viewed, or "" when none is set (the demo before login, a test). */
const siteSuffix = (): string => {
  try {
    const id = storage.get("siteId");

    return typeof id === "string" && id ? `.${id}` : "";
  } catch (e) {
    return "";
  }
};
const stepsKey = () => `${STEPS_KEY}${siteSuffix()}`;
const modeKey = () => `${MODE_KEY}${siteSuffix()}`;

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;   // storage may be blocked: the funnel then lasts until the page is closed
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (e) {
    /* blocked: nothing to do */
  }
};

/**
 * The demo is OFF until the address has ?funnel=1 (the public demo shows the plain dashboard by default). A STAGING build (REACT_APP_FUNNEL=1, no demo) is ON by default, because staging
 * is where the funnel is being tested and a switch nobody knows about reads as "where is it?"; ?funnel=0 turns it off for this browser (remembered), ?funnel=1 back on.
 */
const onByDefault = (): boolean => process.env.REACT_APP_FUNNEL === "1" && process.env.REACT_APP_DEMO !== "1";
export const isFunnelEnabled = (): boolean => {
  if (enabled !== null) return enabled;
  let param: string | null = null;
  try {
    param = new URLSearchParams(window.location.search).get("funnel");
  } catch (e) {
    param = null;
  }
  if (param === "1") write(ENABLED_KEY, "1");
  if (param === "0") write(ENABLED_KEY, onByDefault() ? "0" : null);
  const saved = read(ENABLED_KEY);
  enabled = saved === "1" || (saved !== "0" && onByDefault());

  return enabled;
};

export const setGoalProvider = (fn: () => string | null) => {
  goalProvider = fn;
};
export const goalPage = (): string | null => goalProvider() || null;

const clean = (list: unknown): string[] => {
  const goal = goalPage();
  const seen = new Set<string>();
  const out: string[] = [];
  (Array.isArray(list) ? list : []).forEach((p) => {
    if (typeof p === "string" && p && p !== goal && !seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  });

  return out;
};

const load = () => {
  const key = stepsKey();
  if (loadedFrom === key) return;
  loadedFrom = key;
  try {
    steps = clean(JSON.parse(read(key) ?? read(STEPS_KEY) ?? "[]"));
  } catch (e) {
    steps = [];   // a damaged saved value is not a reason to break the dashboard
  }
};

const notify = () => eventEmitter.emit("funnelChanged", { building });
const save = () => write(stepsKey(), JSON.stringify(steps));

/**
 * STRICT or LOOSE (Chris's terms): does a visitor have to go straight from each step to the next, or may they wander between steps as long as they reach them in order? It changes what the
 * NUMBERS mean (funnelProgress.ts), so it is a property of the funnel, not a view option. Loose is the default: it is how people tend to think of a funnel, and a visitor who wandered
 * off but is still on the site is not lost.
 */
let mode: FunnelMode | null = null;
let modeFrom: string | null = null;
export const getMode = (): FunnelMode => {
  const key = modeKey();
  if (mode && modeFrom === key) return mode;
  modeFrom = key;
  mode = (read(key) ?? read(MODE_KEY)) === "strict" ? "strict" : "loose";

  return mode;
};
export const setMode = (next: FunnelMode) => {
  if (next === getMode()) return;
  mode = next;
  modeFrom = modeKey();
  write(modeFrom, next);
  notify();
};

/** How the funnel is shown on the canvas. Each part can be switched off on its own, and the choice is remembered in this browser. */
export interface FunnelView {
  /** the ribbons between the step boxes */
  ribbons: boolean;
  /** dim every box that is not part of the funnel, so the funnel stands out */
  dim: boolean;
  /** colour each ribbon by how much is lost at its step (otherwise one neutral blue) */
  byLoss: boolean;
  /** a visitor who steps from one funnel step to the next travels ALONG the ribbon between the two boxes */
  ride: boolean;
}
const DEFAULT_VIEW: FunnelView = { ribbons: true, dim: true, byLoss: true, ride: true };
let view: FunnelView | null = null;

export const getView = (): FunnelView => {
  if (view) return { ...view };
  let saved: Partial<FunnelView> = {};
  try {
    saved = JSON.parse(read(VIEW_KEY) || "{}") || {};
  } catch (e) {
    saved = {};   // a damaged saved value falls back to the defaults
  }
  const flag = (v: unknown, fallback: boolean) => (typeof v === "boolean" ? v : fallback);
  view = {
    ribbons: flag(saved.ribbons, DEFAULT_VIEW.ribbons),
    dim: flag(saved.dim, DEFAULT_VIEW.dim),
    byLoss: flag(saved.byLoss, DEFAULT_VIEW.byLoss),
    ride: flag(saved.ride, DEFAULT_VIEW.ride),
  };

  return { ...view };
};

export const setView = (patch: Partial<FunnelView>) => {
  view = { ...getView(), ...patch };
  write(VIEW_KEY, JSON.stringify(view));
  notify();
};

/** The saved funnel (the steps before the goal). */
export const getSteps = (): string[] => {
  load();

  return steps.slice();
};
/** What is being shown right now: the draft while picking, otherwise the saved funnel. */
export const activeSteps = (): string[] => {
  load();

  return building ? draft.slice() : steps.slice();
};
export const isBuilding = () => building;

export const setSteps = (next: string[]) => {
  load();
  steps = clean(next);
  save();
  notify();
};

export const addStep = (page: string) => {
  load();
  const target = building ? draft : steps;
  if (!page || page === goalPage() || target.includes(page)) return;
  target.push(page);
  if (!building) save();
  notify();
};
export const removeStep = (index: number) => {
  load();
  const target = building ? draft : steps;
  if (index < 0 || index >= target.length) return;
  target.splice(index, 1);
  if (!building) save();
  notify();
};
export const moveStep = (index: number, by: number) => {
  load();
  const target = building ? draft : steps;
  const to = index + by;
  if (index < 0 || to < 0 || index >= target.length || to >= target.length) return;
  const [item] = target.splice(index, 1);
  target.splice(to, 0, item);
  if (!building) save();
  notify();
};
export const reorderStep = (from: number, to: number) => {
  load();
  if (building || from === to || from < 0 || to < 0 || from >= steps.length || to >= steps.length) return;
  const [item] = steps.splice(from, 1);
  steps.splice(to, 0, item);
  save();
  notify();
};
export const clearFunnel = () => {
  load();
  steps = [];
  save();
  notify();
};

/** "Start a new funnel": the next clicks on page boxes add steps. The saved funnel is untouched until it is finished, so Cancel puts everything back. */
export const startBuilding = () => {
  load();
  building = true;
  draft = [];
  notify();
};
export const undoPick = () => {
  if (!building || !draft.length) return;
  draft.pop();
  notify();
};
export const cancelBuilding = () => {
  if (!building) return;
  building = false;
  draft = [];
  notify();
};
/** Finishing keeps what was picked (the goal is implicitly last). Nothing picked = nothing to keep, so it stays in picking mode. */
export const finishBuilding = (): boolean => {
  if (!building || !draft.length) return false;
  steps = clean(draft);
  building = false;
  draft = [];
  save();
  notify();

  return true;
};

/**
 * A page box was clicked. Returns true when the click was used up by picking mode (the caller must then NOT zoom into the box). Clicking the goal's box finishes the funnel, since the goal
 * is where a funnel ends; a page already picked is ignored.
 */
export const pickPage = (page: string): boolean => {
  if (!building) return false;
  const goal = goalPage();
  if (goal && page === goal) {
    finishBuilding();

    return true;
  }
  if (!draft.includes(page)) {
    draft.push(page);
    notify();
  }

  return true;
};

/** For tests only: forget everything, including what was read from storage. */
export const resetFunnelStateForTests = () => {
  enabled = null;
  steps = [];
  draft = [];
  building = false;
  loadedFrom = null;
  mode = null;
  modeFrom = null;
  view = null;
  goalProvider = () => null;
};
